import { useRef, useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { COMPANY } from "../i18n/translations";
import Seo from "../components/Seo";

const API = "/api";
const MAP_QUERY = encodeURIComponent(
  "Str. Gării 53, Făurei 815100, Brăila, Romania",
);
const MAP_SRC = `https://www.google.com/maps?q=${MAP_QUERY}&hl=ro&z=15&output=embed`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  company: "",
  subject: "",
  message: "",
};

const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(initialForm);
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  // useRef initializer already runs at first render — no effect needed.
  const mountedAt = useRef(Date.now());

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = true;
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = true;
    if (!form.message || form.message.trim().length < 5) e.message = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error(t.contact.form.error);
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          language: lang,
          website,
          elapsed_ms: Date.now() - mountedAt.current,
        }),
      });
      if (!response.ok) throw new Error(`Contact request failed: ${response.status}`);
      setSubmitted(true);
      setForm(initialForm);
      setWebsite("");
      toast.success(t.contact.form.success);
    } catch (err) {
      toast.error(t.contact.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass = (name) =>
    `w-full bg-[#050505] border ${
      errors[name] ? "border-[#FF4500]" : "border-white/15"
    } px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FF4500] transition-colors text-sm`;

  return (
    <div data-testid="contact-page">
      <Seo pageKey="contact" />
      {/* Hero */}
      <section className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-25" />
        <div className="relative mx-auto max-w-[1400px] px-5 lg:px-10 pt-20 lg:pt-28 pb-12 lg:pb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="block w-10 h-px bg-[#FF4500]" />
            <span className="text-overline text-[#FF4500]">{t.contact.eyebrow}</span>
          </div>
          <h1
            data-testid="contact-title"
            className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.9] text-white max-w-5xl whitespace-pre-line"
          >
            {t.contact.title}
          </h1>
          <p className="mt-8 text-white/70 max-w-2xl leading-relaxed">{t.contact.lead}</p>
        </div>
      </section>

      {/* Form + Info */}
      <section className="bg-[#050505]">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="border border-white/10 bg-[#0a0a0a] p-6 lg:p-10">
              {submitted ? (
                <div data-testid="contact-success" className="py-10 text-center">
                  <CheckCircle2 className="w-12 h-12 text-[#FF4500] mx-auto" />
                  <h3 className="mt-6 font-display text-3xl uppercase text-white">
                    {t.contact.form.success}
                  </h3>
                  <button
                    data-testid="contact-send-another"
                    onClick={() => setSubmitted(false)}
                    className="mt-8 inline-flex items-center gap-2 border border-white/30 hover:border-white text-white px-5 py-3 font-bold uppercase tracking-widest text-xs transition-colors"
                  >
                    {t.contact.form.submit}
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="contact-form" noValidate>
                  {/* Honeypot: hidden from real users, bots fill it */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-10000px",
                      top: "auto",
                      width: 1,
                      height: 1,
                      overflow: "hidden",
                    }}
                  >
                    <label>
                      Website (leave empty)
                      <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        name="website"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                      />
                    </label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-overline text-white/60 block mb-2">
                        {t.contact.form.name} *
                      </label>
                      <input
                        data-testid="contact-name"
                        name="name"
                        value={form.name}
                        onChange={onChange}
                        placeholder={t.contact.form.namePh}
                        className={fieldClass("name")}
                      />
                    </div>
                    <div>
                      <label className="text-overline text-white/60 block mb-2">
                        {t.contact.form.email} *
                      </label>
                      <input
                        data-testid="contact-email"
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={onChange}
                        placeholder={t.contact.form.emailPh}
                        className={fieldClass("email")}
                      />
                    </div>
                    <div>
                      <label className="text-overline text-white/60 block mb-2">
                        {t.contact.form.phone}
                      </label>
                      <input
                        data-testid="contact-phone"
                        name="phone"
                        value={form.phone}
                        onChange={onChange}
                        placeholder={t.contact.form.phonePh}
                        className={fieldClass("phone")}
                      />
                    </div>
                    <div>
                      <label className="text-overline text-white/60 block mb-2">
                        {t.contact.form.company}
                      </label>
                      <input
                        data-testid="contact-company"
                        name="company"
                        value={form.company}
                        onChange={onChange}
                        placeholder={t.contact.form.companyPh}
                        className={fieldClass("company")}
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="text-overline text-white/60 block mb-2">
                      {t.contact.form.subject}
                    </label>
                    <input
                      data-testid="contact-subject"
                      name="subject"
                      value={form.subject}
                      onChange={onChange}
                      placeholder={t.contact.form.subjectPh}
                      className={fieldClass("subject")}
                    />
                  </div>

                  <div className="mt-4">
                    <label className="text-overline text-white/60 block mb-2">
                      {t.contact.form.message} *
                    </label>
                    <textarea
                      data-testid="contact-message"
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      placeholder={t.contact.form.messagePh}
                      rows={6}
                      className={fieldClass("message") + " resize-none"}
                    />
                  </div>

                  <button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={submitting}
                    className="mt-6 inline-flex items-center gap-3 bg-[#FF4500] hover:bg-[#FF5722] disabled:opacity-60 disabled:cursor-not-allowed text-black px-6 py-4 font-bold uppercase tracking-widest text-sm transition-colors"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t.contact.form.sending}
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        {t.contact.form.submit}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="lg:col-span-5">
            <div className="border border-white/10 bg-[#0a0a0a] p-6 lg:p-10 h-full">
              <p className="text-overline text-[#FF4500]">{t.common.tagline}</p>
              <h3 className="mt-3 font-display text-3xl uppercase text-white">
                Conveyor Belts S.R.L.
              </h3>

              <ul className="mt-8 space-y-6">
                <li className="flex items-start gap-4">
                  <span className="w-10 h-10 border border-[#FF4500] flex items-center justify-center flex-none">
                    <Phone className="w-4 h-4 text-[#FF4500]" />
                  </span>
                  <div>
                    <p className="text-overline text-white/50">{t.contact.info.phoneL}</p>
                    <a
                      href={`tel:${COMPANY.phoneDial}`}
                      data-testid="contact-info-phone"
                      className="text-white text-lg hover:text-[#FF4500]"
                    >
                      {COMPANY.phone}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-10 h-10 border border-[#FF4500] flex items-center justify-center flex-none">
                    <Mail className="w-4 h-4 text-[#FF4500]" />
                  </span>
                  <div>
                    <p className="text-overline text-white/50">{t.contact.info.emailL}</p>
                    <a
                      href={`mailto:${COMPANY.email}`}
                      data-testid="contact-info-email"
                      className="text-white text-lg hover:text-[#FF4500] break-all"
                    >
                      {COMPANY.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-10 h-10 border border-[#FF4500] flex items-center justify-center flex-none">
                    <MapPin className="w-4 h-4 text-[#FF4500]" />
                  </span>
                  <div>
                    <p className="text-overline text-white/50">{t.contact.info.addressL}</p>
                    <p className="text-white text-base">{t.contact.info.address}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-10 h-10 border border-[#FF4500] flex items-center justify-center flex-none">
                    <Clock className="w-4 h-4 text-[#FF4500]" />
                  </span>
                  <div>
                    <p className="text-overline text-white/50">{t.contact.info.hoursL}</p>
                    <p className="text-white text-base">{t.contact.info.hours}</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="border-t border-white/10 bg-[#0a0a0a]">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16 lg:py-20">
          <div className="flex items-center gap-3 mb-6">
            <span className="block w-10 h-px bg-[#FF4500]" />
            <span className="text-overline text-[#FF4500]">
              {t.contact.info.addressL} · {t.contact.info.address}
            </span>
          </div>
          <div
            data-testid="contact-map"
            className="relative border border-white/10 overflow-hidden"
            style={{ aspectRatio: "21 / 9" }}
          >
            <div className="absolute inset-0 bg-[#050505] flex items-center justify-center">
              <MapPin className="w-8 h-8 text-[#FF4500] animate-pulse" />
            </div>
            <iframe
              title="Conveyor Belts S.R.L. - Făurei"
              src={MAP_SRC}
              width="100%"
              height="100%"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="relative w-full h-full grayscale contrast-125"
              style={{ border: 0, filter: "grayscale(1) contrast(1.2) invert(0.92) hue-rotate(180deg)" }}
            />
          </div>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${MAP_QUERY}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-directions"
            className="mt-6 inline-flex items-center gap-2 text-[#FF4500] hover:text-[#FF5722] text-sm font-bold uppercase tracking-widest"
          >
            <MapPin className="w-4 h-4" />
            {lang === "ro" ? "Deschide în Google Maps" : "Open in Google Maps"}
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
